"use client";
import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useCostCodeMap } from "./useCostCodeMap";
const API = "https://erp-production-72da01c8e651.herokuapp.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtQty = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const cls = (...c) => c.filter(Boolean).join(" ");

const COVERAGE_UOMS = [
  "sf",
  "lf",
  "sq ft",
  "lin ft",
  "square",
  "linear",
  "sheet",
];
const isCoverageUom = (uom) =>
  COVERAGE_UOMS.some((u) => (uom || "").toLowerCase().includes(u));
const isSF = (uom) =>
  ["sf", "sq ft", "square"].some((u) => (uom || "").toLowerCase().includes(u));
const isLF = (uom) =>
  ["lf", "lin ft", "linear"].some((u) => (uom || "").toLowerCase().includes(u));

const normalizedUnitCost = (item) => {
  const raw = Math.round(Number(item.unitCost || 0) * 100) / 100;
  const cov = Number(item.coveragePerUnit || 1);
  return (raw * cov).toFixed(2);
};

// ── Master grouping key ───────────────────────────────────────────────────
// Groups by itemCode + section ONLY. This is the fix: previously the key
// also folded in normalizedUnitCost, which meant the same item ordered at
// two different sizes/prices (e.g. a CO row at $1.70/LF and manual rows at
// $1.51/LF, different sizes) would fracture into separate top-level rows
// instead of rolling up under one tracked item. Regardless of size or price
// variance, an item code within a section is now always one row, compared
// against its combined budget + CO + manual allocation.
const masterGroupKey = (item) => {
  const code = (item.itemCode || "").trim().toLowerCase();
  const section = (item.sectionNumber || "").trim().toLowerCase();
  return `${code}||${section}`;
};

// Used only to detect size/price variants WITHIN a group, for the
// expandable "Size / Price Variants" sub-panel — not for splitting rows.
const variantKey = (item) => {
  const size = (item.size || "").trim().toLowerCase();
  const cost = normalizedUnitCost(item);
  return `${size}||${cost}`;
};

// ── Summary card ──────────────────────────────────────────────────────────────
function SummaryCard({ label, value, color = "text-white", sub, flag }) {
  return (
    <div
      className={cls(
        "rounded-xl border p-4",
        flag === "red"
          ? "bg-red-950 border-red-700"
          : flag === "green"
            ? "bg-green-950 border-green-700"
            : "bg-gray-800 border-gray-700",
      )}
    >
      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
        {label}
      </p>
      <p className={cls("text-xl font-bold font-mono", color)}>${fmt(value)}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Progress bar ──────────────────────────────────────────────────────────────
function QtyBar({ ordered, delivered, netQty }) {
  const target = netQty > 0 ? netQty : ordered;
  const orderedPct = target > 0 ? Math.min(100, (ordered / target) * 100) : 0;
  const deliveredPct =
    target > 0 ? Math.min(100, (delivered / target) * 100) : 0;
  const isOver = ordered > target && target > 0;
  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <div className="relative flex-1 bg-gray-700 rounded-full h-2 overflow-hidden">
        <div
          className={cls(
            "absolute left-0 top-0 h-full rounded-full",
            isOver ? "bg-red-500/50" : "bg-yellow-600/40",
          )}
          style={{ width: `${orderedPct}%` }}
        />
        <div
          className={cls(
            "absolute left-0 top-0 h-full rounded-full transition-all",
            deliveredPct >= 100 ? "bg-green-500" : "bg-orange-500",
          )}
          style={{ width: `${deliveredPct}%` }}
        />
      </div>
      <span className="text-[10px] text-gray-400 shrink-0 font-mono whitespace-nowrap">
        {fmtQty(delivered)}/{fmtQty(target)}
      </span>
    </div>
  );
}

// ── Item variant breakdown panel (NEW) ────────────────────────────────────
// Shows the size/price entries that were rolled up into this consolidated
// row, so users can still see "why" the net qty/cost is what it is, without
// the top-level table fragmenting into duplicate item-code rows.
function ItemVariantPanel({ variants }) {
  if (!variants || variants.length <= 1) return null;
  const sourceLabel = {
    budget: "Budget",
    change_order: "CO",
    manual: "Unbudgeted",
  };
  return (
    <div className="rounded-lg border border-gray-700 overflow-hidden">
      <div className="bg-gray-800 px-3 py-1.5 border-b border-gray-700 flex items-center gap-2">
        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
          Size / Price Variants
        </span>
        <span className="text-[10px] text-gray-600">
          — {variants.length} distinct entries rolled into this item
        </span>
      </div>
      <table className="w-full text-xs">
        <thead className="bg-gray-800/50">
          <tr>
            <th className="px-3 py-1.5 text-left text-gray-500 font-medium">
              Size
            </th>
            <th className="px-3 py-1.5 text-right text-gray-500 font-medium">
              Unit Cost
            </th>
            <th className="px-3 py-1.5 text-right text-gray-500 font-medium">
              Coverage
            </th>
            <th className="px-3 py-1.5 text-left text-gray-500 font-medium">
              Source
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700/40">
          {variants.map((v, i) => (
            <tr
              key={i}
              className={i % 2 === 0 ? "bg-gray-900/30" : "bg-gray-800/20"}
            >
              <td className="px-3 py-1.5 text-gray-300">{v.size || "—"}</td>
              <td className="px-3 py-1.5 text-right text-gray-300 font-mono">
                ${fmt(v.unitCost)}
              </td>
              <td className="px-3 py-1.5 text-right text-gray-400 font-mono">
                {v.coveragePerUnit ? `×${v.coveragePerUnit}` : "—"}
              </td>
              <td className="px-3 py-1.5">
                <span
                  className={cls(
                    "text-[9px] px-1.5 py-0.5 rounded font-semibold",
                    v.source === "change_order"
                      ? "bg-purple-700/60 text-purple-100"
                      : v.source === "manual"
                        ? "bg-yellow-700/60 text-yellow-100"
                        : "bg-gray-700 text-gray-300",
                  )}
                >
                  {sourceLabel[v.source] || v.source}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Expanded detail panel ─────────────────────────────────────────────────────
function ExpandedDetailPanel({ row, deliveryHistory, colSpan }) {
  const coRows = row._coRows || [];
  const allIds = row._allIds || [];
  const records = allIds.flatMap((id) => deliveryHistory[id] || []);
  const seen = new Set();
  const moRecords = records.filter((r) => {
    const k = `${r.orderNumber}-${r.lineIndex ?? r.vendor}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  const hasCOs = coRows.length > 0;
  const hasMOs = moRecords.length > 0;
  const hasVariants = (row._variants || []).length > 1;

  return (
    <tr className="bg-gray-950/60">
      <td colSpan={colSpan} className="px-4 py-3">
        <div className="ml-6 space-y-3">
          {hasVariants && <ItemVariantPanel variants={row._variants} />}

          {hasCOs && (
            <div className="rounded-lg border border-purple-800/40 overflow-hidden">
              <div className="bg-purple-950/40 px-3 py-1.5 border-b border-purple-800/30 flex items-center gap-2">
                <span className="text-[10px] font-semibold text-purple-300 uppercase tracking-wide">
                  Change Order Details
                </span>
                <span className="text-[10px] text-purple-600">
                  — {coRows.length} CO{coRows.length !== 1 ? "s" : ""}
                </span>
              </div>
              <table className="w-full text-xs">
                <thead className="bg-purple-950/20">
                  <tr>
                    <th className="px-3 py-1.5 text-left text-purple-400/70 font-medium">
                      CO Ref
                    </th>
                    <th className="px-3 py-1.5 text-left text-purple-400/70 font-medium">
                      Bid Area
                    </th>
                    <th className="px-3 py-1.5 text-left text-purple-400/70 font-medium">
                      Section
                    </th>
                    <th className="px-3 py-1.5 text-right text-purple-400/70 font-medium">
                      Δ Qty
                    </th>
                    <th className="px-3 py-1.5 text-right text-purple-400/70 font-medium">
                      Unit Cost
                    </th>
                    <th className="px-3 py-1.5 text-right text-purple-400/70 font-medium">
                      Coverage
                    </th>
                    <th className="px-3 py-1.5 text-right text-purple-400/70 font-medium">
                      Δ Amount
                    </th>
                    <th className="px-3 py-1.5 text-left text-purple-400/70 font-medium">
                      UOM
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-purple-900/30">
                  {coRows.map((co, i) => {
                    const lf = isCoverageUom(co.uom || co.container);
                    const covLF = lf
                      ? (co.deltaQty || 0) * (co.coveragePerUnit || 1)
                      : null;
                    return (
                      <tr
                        key={i}
                        className={
                          i % 2 === 0 ? "bg-purple-950/10" : "bg-purple-950/5"
                        }
                      >
                        <td className="px-3 py-2 font-mono text-purple-300 font-semibold">
                          {co.changeOrderRef || "—"}
                        </td>
                        <td className="px-3 py-2 text-gray-400">
                          {co.bidArea || "—"}
                        </td>
                        <td className="px-3 py-2 text-gray-400 font-mono">
                          {co.sectionNumber || "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-semibold">
                          <span
                            className={
                              (co.deltaQty || 0) >= 0
                                ? "text-green-400"
                                : "text-red-400"
                            }
                          >
                            {(co.deltaQty || 0) >= 0 ? "+" : ""}
                            {fmtQty(co.deltaQty || 0)}
                          </span>
                          {covLF !== null && (
                            <span className="block text-[9px] text-gray-500">
                              = {fmtQty(covLF)} {(co.uom || "").toUpperCase()}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-300 font-mono">
                          ${fmt(co.unitCost || 0)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-400 font-mono">
                          {co.coveragePerUnit ? `×${co.coveragePerUnit}` : "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-semibold">
                          <span
                            className={
                              (co.amount || 0) >= 0
                                ? "text-purple-300"
                                : "text-red-400"
                            }
                          >
                            {(co.amount || 0) >= 0 ? "+" : ""}$
                            {fmt(co.amount || 0)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-400">
                          {co.uom || co.container || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {coRows.length > 1 && (
                  <tfoot className="bg-purple-950/30 border-t border-purple-800/30">
                    <tr>
                      <td
                        colSpan={3}
                        className="px-3 py-1.5 text-xs text-purple-400/60"
                      >
                        Total CO impact
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono font-semibold">
                        <span
                          className={
                            row._totalCODeltaQty >= 0
                              ? "text-green-400"
                              : "text-red-400"
                          }
                        >
                          {row._totalCODeltaQty >= 0 ? "+" : ""}
                          {fmtQty(row._totalCODeltaQty)}
                        </span>
                      </td>
                      <td colSpan={2} />
                      <td className="px-3 py-1.5 text-right font-mono font-semibold">
                        <span
                          className={
                            row._totalCOAmount >= 0
                              ? "text-purple-300"
                              : "text-red-400"
                          }
                        >
                          {row._totalCOAmount >= 0 ? "+" : ""}$
                          {fmt(row._totalCOAmount)}
                        </span>
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          <div className="rounded-lg border border-gray-700 overflow-hidden">
            <div className="bg-gray-800 px-3 py-1.5 border-b border-gray-700 flex items-center gap-2">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                Material Orders
              </span>
              <span className="text-[10px] text-gray-600">
                —{" "}
                {hasMOs
                  ? `${moRecords.length} order${moRecords.length !== 1 ? "s" : ""}`
                  : "none yet"}
              </span>
            </div>
            {hasMOs ? (
              <table className="w-full text-xs">
                <thead className="bg-gray-800/50">
                  <tr>
                    <th className="px-3 py-1.5 text-left text-gray-500 font-medium">
                      Order #
                    </th>
                    <th className="px-3 py-1.5 text-left text-gray-500 font-medium">
                      Vendor
                    </th>
                    <th className="px-3 py-1.5 text-center text-gray-500 font-medium">
                      Status
                    </th>
                    <th className="px-3 py-1.5 text-right text-gray-500 font-medium">
                      Ordered
                    </th>
                    <th className="px-3 py-1.5 text-right text-gray-500 font-medium">
                      Delivered
                    </th>
                    <th className="px-3 py-1.5 text-right text-gray-500 font-medium">
                      Unit Price
                    </th>
                    <th className="px-3 py-1.5 text-right text-gray-500 font-medium">
                      Delivered $
                    </th>
                    <th className="px-3 py-1.5 text-left text-gray-500 font-medium">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/40">
                  {moRecords.map((r, i) => {
                    const statusColor =
                      {
                        delivered: "bg-green-700 text-green-200",
                        partial: "bg-orange-700 text-orange-200",
                        confirmed: "bg-blue-700 text-blue-200",
                        pending: "bg-yellow-700 text-yellow-200",
                        void: "bg-red-800/60 text-red-300",
                      }[r.status] || "bg-gray-700 text-gray-400";
                    return (
                      <tr
                        key={i}
                        className={
                          i % 2 === 0 ? "bg-gray-900/30" : "bg-gray-800/20"
                        }
                      >
                        <td className="px-3 py-1.5 font-mono text-orange-400">
                          {r.orderNumber}
                        </td>
                        <td className="px-3 py-1.5 text-gray-300">
                          {r.vendor}
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          <span
                            className={cls(
                              "px-1.5 py-0.5 text-[9px] font-semibold rounded",
                              statusColor,
                            )}
                          >
                            {r.status?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-right text-gray-300 font-mono">
                          {fmtQty(r.qtyOrdered)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-green-300 font-mono">
                          {fmtQty(r.qtyDelivered)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-gray-300 font-mono">
                          ${fmt(r.unitPrice)}
                        </td>
                        <td className="px-3 py-1.5 text-right text-blue-300 font-mono font-semibold">
                          ${fmt(r.total)}
                        </td>
                        <td className="px-3 py-1.5 text-gray-500">
                          {r.deliveredAt
                            ? new Date(r.deliveredAt).toLocaleDateString()
                            : new Date(r.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-800 border-t border-gray-700">
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-1.5 text-xs text-gray-500"
                    >
                      Total delivered
                    </td>
                    <td className="px-3 py-1.5 text-right text-sm font-bold text-blue-300 font-mono">
                      ${fmt(moRecords.reduce((s, r) => s + (r.total || 0), 0))}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            ) : (
              <p className="px-4 py-3 text-xs text-gray-500 italic">
                No material orders placed for this item yet.
              </p>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

// ── Progress bar helper ───────────────────────────────────────────────────────
function ProgressBar({ label, pct, colorClass }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[9px] text-gray-500 w-28 shrink-0">{label}</span>
      <div className="flex-1 bg-gray-700 rounded-full h-1.5 overflow-hidden">
        <div
          className={cls("h-full rounded-full transition-all", colorClass)}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
      <span className="text-[9px] text-gray-500 font-mono w-8 text-right shrink-0">
        {pct.toFixed(0)}%
      </span>
    </div>
  );
}

// ── Coverage sub-panel ────────────────────────────────────────────────────────
function CoveragePanelPanel({
  label,
  uom,
  est,
  ordered,
  delivered,
  colorScheme,
}) {
  const ordPct = est > 0 ? Math.min(100, (ordered / est) * 100) : 0;
  const delPct = est > 0 ? Math.min(100, (delivered / est) * 100) : 0;
  const isOrderedOver = ordered > est && est > 0;
  const isDeliveredOver = delivered > est && est > 0;
  const borderCls =
    colorScheme === "sf"
      ? "bg-sky-950/40 border-sky-800/40"
      : "bg-teal-950/40 border-teal-800/40";
  const labelCls = colorScheme === "sf" ? "text-sky-400" : "text-teal-400";
  return (
    <div className={cls("mx-4 mb-2 rounded-lg border px-3 py-2", borderCls)}>
      <div className="flex items-center gap-3">
        <p
          className={cls(
            "text-[9px] font-semibold uppercase tracking-wide shrink-0",
            labelCls,
          )}
        >
          {label}
        </p>
        <span className="text-[10px] text-gray-500 font-mono shrink-0">
          Est <span className="text-blue-300 font-bold">{fmtQty(est)}</span>
        </span>
        <span className="text-[10px] text-gray-500 font-mono shrink-0">
          Ord{" "}
          <span
            className={cls(
              "font-bold",
              isOrderedOver ? "text-red-400" : "text-yellow-300",
            )}
          >
            {fmtQty(ordered)}
          </span>
        </span>
        <span className="text-[10px] text-gray-500 font-mono shrink-0">
          Del{" "}
          <span
            className={cls(
              "font-bold",
              isDeliveredOver ? "text-red-400" : "text-green-300",
            )}
          >
            {fmtQty(delivered)}
          </span>
        </span>
        <div className="relative flex-1 bg-gray-700 rounded-full h-2 overflow-hidden">
          <div
            className={cls(
              "absolute left-0 top-0 h-full rounded-full",
              isOrderedOver ? "bg-red-500/40" : "bg-yellow-600/40",
            )}
            style={{ width: `${ordPct}%` }}
          />
          <div
            className={cls(
              "absolute left-0 top-0 h-full rounded-full transition-all",
              isDeliveredOver ? "bg-red-500" : "bg-green-500",
            )}
            style={{ width: `${delPct}%` }}
          />
        </div>
        <span className="text-[10px] font-mono shrink-0">
          {isOrderedOver ? (
            <span className="text-red-300 font-semibold">
              ⚠ +{fmtQty(ordered - est)} {uom}
            </span>
          ) : isDeliveredOver ? (
            <span className="text-red-300 font-semibold">
              ⚠ +{fmtQty(delivered - est)} {uom}
            </span>
          ) : (
            <span className="text-gray-500">
              {fmtQty(delivered)}/{fmtQty(est)} {uom}
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

// ── Inline rename component ───────────────────────────────────────────────────
function InlineRename({ value, onSave, onCancel, saving }) {
  const [val, setVal] = useState(value);
  const inputRef = useRef(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  return (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onSave(val);
          if (e.key === "Escape") onCancel();
        }}
        className="px-2 py-1 text-base font-bold bg-gray-700 border border-orange-500 rounded-lg text-white focus:outline-none min-w-[240px]"
      />
      <button
        onClick={() => onSave(val)}
        disabled={saving || !val.trim()}
        className="px-2.5 py-1 text-xs bg-orange-500 hover:bg-orange-600 text-white rounded font-semibold disabled:opacity-50 flex items-center gap-1"
      >
        {saving ? (
          <span className="animate-spin inline-block w-3 h-3 border-2 border-white/30 border-t-white rounded-full" />
        ) : (
          "Save"
        )}
      </button>
      <button
        onClick={onCancel}
        className="px-2 py-1 text-xs text-gray-400 hover:text-white border border-gray-600 rounded"
      >
        ✕
      </button>
    </div>
  );
}

// ── Move-to dropdown (single row) ─────────────────────────────────────────────
function MoveDropdown({ rowKey, siblingPOs, onMove, moving }) {
  if (!siblingPOs.length)
    return <span className="text-[9px] text-gray-700 italic">Only PO</span>;
  return (
    <select
      defaultValue=""
      onChange={(e) => {
        if (e.target.value) {
          onMove(rowKey, e.target.value);
          e.target.value = "";
        }
      }}
      disabled={moving}
      onClick={(e) => e.stopPropagation()}
      className="px-1.5 py-0.5 text-[10px] bg-gray-700 border border-gray-600 rounded text-gray-300 focus:border-orange-500 focus:outline-none w-full disabled:opacity-40 cursor-pointer"
    >
      <option value="">Move to…</option>
      {siblingPOs.map((p) => (
        <option key={p._id} value={p._id}>
          {p.title}
        </option>
      ))}
    </select>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
export default function PODetailView3() {
  const { id: projectId, poId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };
  const { resolveName, resolveLabel } = useCostCodeMap();

  const [po, setPo] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deliveryHistory, setDeliveryHistory] = useState({});
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [activeTab, setActiveTab] = useState("summary");
  const [search, setSearch] = useState("");
  const [sectionFilter, setSectionFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");

  // ── Edit mode ───────────────────────────────────────────────────────────────
  const [editMode, setEditMode] = useState(false);

  // ── Rename state ────────────────────────────────────────────────────────────
  const [renaming, setRenaming] = useState(false);
  const [renameSaving, setRenameSaving] = useState(false);

  // ── Sibling POs (for move-to) ───────────────────────────────────────────────
  const [siblingPOs, setSiblingPOs] = useState([]);

  // ── Selection state ─────────────────────────────────────────────────────────
  const [selected, setSelected] = useState(new Set()); // rowKeys
  const [moving, setMoving] = useState(false);

  // ── Load PO + items ─────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const [poRes, itemsRes] = await Promise.all([
          axios.get(`${API}/purchaseOrders3/${poId}`, { headers }),
          axios.get(`${API}/purchaseOrders3/${poId}/items?limit=1000`, {
            headers,
          }),
        ]);
        setPo(poRes.data.purchaseOrder);
        setItems(itemsRes.data.items || []);
        setLoadingHistory(true);
        try {
          const histRes = await axios.get(
            `${API}/purchaseOrders3/${poId}/deliveries`,
            { headers },
          );
          setDeliveryHistory(histRes.data.historyByBudgetItem || {});
        } catch (e) {
          console.warn("Delivery history unavailable:", e.message);
        } finally {
          setLoadingHistory(false);
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [poId]);

  // ── Load sibling POs for move-to ────────────────────────────────────────────
  useEffect(() => {
    if (!editMode || !projectId || siblingPOs.length > 0) return;
    axios
      .get(`${API}/purchaseOrders3/project/${projectId}`, { headers })
      .then((res) =>
        setSiblingPOs(
          (res.data.purchaseOrders || []).filter((p) => p._id !== poId),
        ),
      )
      .catch(() => {});
  }, [editMode, projectId, poId]);

  // ── Rename handler ──────────────────────────────────────────────────────────
  const handleRename = async (newTitle) => {
    const val = newTitle.trim();
    if (!val || val === po.title) {
      setRenaming(false);
      return;
    }
    setRenameSaving(true);
    try {
      await axios.put(
        `${API}/purchaseOrders3/${poId}`,
        { title: val },
        { headers },
      );
      setPo((prev) => ({ ...prev, title: val }));
      setRenaming(false);
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setRenameSaving(false);
    }
  };

  // ── Move items handler ──────────────────────────────────────────────────────
  // rowKey is the _rowKey from consolidatedRows; maps to _allIds
  // We collect all underlying BudgetItem3 _ids for the selected rows and PATCH them.
  const handleMoveRows = async (rowKeys, targetPoId) => {
    const keySet = new Set(typeof rowKeys === "string" ? [rowKeys] : rowKeys);
    // Find all underlying item _ids for these consolidated rows
    const allRawIds = consolidatedRows
      .filter((r) => keySet.has(r._rowKey))
      .flatMap((r) => r._allIds);

    if (!allRawIds.length) return;
    setMoving(true);
    try {
      await axios.patch(
        `${API}/purchaseOrders3/${poId}/items/move`,
        { itemIds: allRawIds, targetPoId },
        { headers },
      );
      // Remove moved items from local state
      const removedIds = new Set(allRawIds);
      setItems((prev) =>
        prev.filter((i) => !removedIds.has(i._id?.toString())),
      );
      setSelected(new Set());
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setMoving(false);
    }
  };

  // ── Selection helpers ───────────────────────────────────────────────────────
  const toggleRow = (key) =>
    setExpandedItem((prev) => (prev === key ? null : key));

  const toggleSelect = (e, rowKey) => {
    e.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(rowKey) ? next.delete(rowKey) : next.add(rowKey);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filteredConsolidated.length) setSelected(new Set());
    else setSelected(new Set(filteredConsolidated.map((r) => r._rowKey)));
  };

  // ── Derived data ────────────────────────────────────────────────────────────
  const uniqueSections = useMemo(() => {
    const s = new Set(items.map((i) => i.sectionNumber).filter(Boolean));
    return ["all", ...[...s].sort()];
  }, [items]);

  const hasCORows = useMemo(
    () => items.some((i) => i.source === "change_order"),
    [items],
  );
  const hasManualRows = useMemo(
    () => items.some((i) => i.source === "manual"),
    [items],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((item) => {
      const matchSection =
        sectionFilter === "all" || item.sectionNumber === sectionFilter;
      const matchSource =
        sourceFilter === "all" || item.source === sourceFilter;
      const matchQ =
        !q ||
        (item.costCodeDescription || item.itemDescription || "")
          .toLowerCase()
          .includes(q) ||
        (item.itemCode || "").toLowerCase().includes(q) ||
        (item.size || "").toLowerCase().includes(q);
      return matchSection && matchSource && matchQ;
    });
  }, [items, search, sectionFilter, sourceFilter]);

  // ══════════════════════════════════════════════════════════════════════════
  // CONSOLIDATION — grouped by itemCode + section (masterGroupKey), NOT by
  // unit cost. Budget + CO + manual rows for the same item code in the same
  // section always roll up into ONE row. Distinct size/price combinations
  // are preserved as `_variants[]` for the expandable detail panel instead
  // of fragmenting the table. Net qty/cost = budget + CO delta + manual, so
  // every item — budgeted or not — is tracked against one running total.
  // ══════════════════════════════════════════════════════════════════════════
  const { consolidatedRows, sectionSummaries } = useMemo(() => {
    const budget = filtered.filter((i) => i.source === "budget");
    const cos = filtered.filter((i) => i.source === "change_order");
    const manual = filtered.filter((i) => i.source === "manual");
    const all = [...budget, ...cos, ...manual];

    const itemById = new Map(all.map((i) => [i._id?.toString(), i]));

    // Group EVERYTHING by itemCode+section up front.
    const groups = new Map();
    all.forEach((item) => {
      const k = masterGroupKey(item);
      if (!groups.has(k)) groups.set(k, { budget: [], co: [], manual: [] });
      const bucket =
        item.source === "change_order"
          ? "co"
          : item.source === "manual"
            ? "manual"
            : "budget";
      groups.get(k)[bucket].push(item);
    });

    const sumActuals = (ids) =>
      ids.reduce(
        (acc, id) => {
          const r = itemById.get(id);
          if (!r) return acc;
          return {
            qtyOrdered: acc.qtyOrdered + (r.qtyOrdered || 0),
            qtyDelivered: acc.qtyDelivered + (r.qtyDelivered || 0),
            qtyPending: acc.qtyPending + (r.qtyPending || 0),
            amountOrdered: acc.amountOrdered + (r.amountOrdered || 0),
            amountDelivered: acc.amountDelivered + (r.amountDelivered || 0),
          };
        },
        {
          qtyOrdered: 0,
          qtyDelivered: 0,
          qtyPending: 0,
          amountOrdered: 0,
          amountDelivered: 0,
        },
      );

    const rows = [];

    groups.forEach(
      ({ budget: budgetItems, co: coItems, manual: manualItems }) => {
        const anchor = budgetItems[0] || coItems[0] || manualItems[0];
        if (!anchor) return;

        const allIds = [
          ...budgetItems.map((i) => i._id?.toString()),
          ...coItems.map((i) => i._id?.toString()),
          ...manualItems.map((i) => i._id?.toString()),
        ].filter(Boolean);

        // Original budgeted qty/cost (0 if this item never appeared in the
        // budget — e.g. it was introduced entirely via CO or manually)
        const origQty = budgetItems.reduce(
          (s, bi) => s + (bi.requiredQty || 0),
          0,
        );
        const origCost = budgetItems.reduce((s, bi) => s + (bi.amount || 0), 0);

        // CO delta — approved change against budget (or the full CO amount if
        // there was no matching budget line for this item code)
        const totalCODeltaQty = coItems.reduce(
          (s, co) => s + (co.deltaQty || 0),
          0,
        );
        const totalCOAmount = coItems.reduce(
          (s, co) => s + (co.amount || 0),
          0,
        );

        // Manual/unbudgeted qty & cost, tracked separately for visibility but
        // still rolled into the same allocated-cost comparison for this item.
        const manualQty = manualItems.reduce(
          (s, m) => s + (m.requiredQty || 0),
          0,
        );
        const manualCost = manualItems.reduce((s, m) => s + (m.amount || 0), 0);

        // "Net" = what this item is actually held accountable to, regardless
        // of which source line(s) it came in on.
        const netQty = origQty + totalCODeltaQty + manualQty;
        const netCost = origCost + totalCOAmount + manualCost;

        // Distinct size/price combos across ALL rows in this group, for the
        // expandable "Size / Price Variants" sub-panel.
        const variantMap = new Map();
        [...budgetItems, ...coItems, ...manualItems].forEach((item) => {
          const vk = variantKey(item);
          if (!variantMap.has(vk))
            variantMap.set(vk, {
              size: item.size || "",
              unitCost: item.unitCost || 0,
              coveragePerUnit: item.coveragePerUnit || 1,
              uom: item.uom || item.container,
              source: item.source,
            });
        });
        const variants = [...variantMap.values()];

        const coRows = coItems.map((co) => ({
          changeOrderRef: co.changeOrderRef,
          bidArea: co.bidArea,
          sectionNumber: co.sectionNumber,
          deltaQty: co.deltaQty || 0,
          unitCost: co.unitCost,
          coveragePerUnit: co.coveragePerUnit,
          amount: co.amount || 0,
          uom: co.uom || co.container,
        }));

        const type =
          budgetItems.length > 0
            ? "budget"
            : coItems.length > 0
              ? "change_order_new"
              : "manual";

        rows.push({
          _rowKey: anchor._id?.toString(),
          _type: type,
          _allIds: allIds,
          _hasCOs: coItems.length > 0,
          _hasManual: manualItems.length > 0,
          _coRows: coRows,
          _variants: variants,
          _totalCODeltaQty: totalCODeltaQty,
          _totalCOAmount: totalCOAmount,
          _manualQty: manualQty,
          _manualCost: manualCost,
          _bidAreas: [
            ...new Set(
              [...budgetItems, ...coItems, ...manualItems]
                .map((i) => i.bidArea)
                .filter(Boolean),
            ),
          ],
          _budgetRows: budgetItems,
          itemCode: anchor.itemCode,
          sectionNumber: anchor.sectionNumber,
          sectionDescription: anchor.sectionDescription,
          sectionTitle: anchor.sectionTitle,
          costCodeDescription: anchor.costCodeDescription,
          itemDescription: anchor.itemDescription,
          size: anchor.size,
          uom: anchor.uom || anchor.container,
          coveragePerUnit: anchor.coveragePerUnit,
          unitCost: anchor.unitCost,
          bidArea: anchor.bidArea,
          changeOrderRef: coRows
            .map((c) => c.changeOrderRef)
            .filter(Boolean)
            .join(", "),
          origQty,
          origCost,
          netQty,
          netCost,
          ...sumActuals(allIds),
        });
      },
    );

    // ── Section summaries ────────────────────────────────────────────────
    const secMap = new Map();
    rows.forEach((row) => {
      const sec = row.sectionNumber || "(none)";
      if (!secMap.has(sec))
        secMap.set(sec, {
          sectionNumber: sec,
          sectionName: row.sectionDescription || row.sectionTitle || null,
          rows: [],
        });
      secMap.get(sec).rows.push(row);
    });

    const summaries = new Map();
    secMap.forEach((sec, secNum) => {
      const estCost = sec.rows.reduce((s, r) => s + (r.origCost || 0), 0);
      const coCost = sec.rows.reduce((s, r) => s + (r._totalCOAmount || 0), 0);
      const manualCostTotal = sec.rows.reduce(
        (s, r) => s + (r._manualCost || 0),
        0,
      );
      const runningTotal = estCost + coCost + manualCostTotal;
      const deliveredCost = sec.rows.reduce(
        (s, r) => s + (r.amountDelivered || 0),
        0,
      );
      const nonCovNetBudget = runningTotal;
      const orderedCost = sec.rows.reduce(
        (s, r) => s + (r.amountOrdered || 0),
        0,
      );
      const covDeliveredCost = sec.rows
        .filter((r) => isCoverageUom(r.uom))
        .reduce((s, r) => s + (r.amountDelivered || 0), 0);

      let sfEst = 0,
        sfOrdered = 0,
        sfDelivered = 0;
      let lfEst = 0,
        lfOrdered = 0,
        lfDelivered = 0;
      sec.rows.forEach((r) => {
        if (!isCoverageUom(r.uom)) return;
        const cov = r.coveragePerUnit || 1;
        if (isSF(r.uom)) {
          sfEst += (r.netQty || 0) * cov;
          sfOrdered += (r.qtyOrdered || 0) * cov;
          sfDelivered += (r.qtyDelivered || 0) * cov;
        } else if (isLF(r.uom)) {
          lfEst += (r.netQty || 0) * cov;
          lfOrdered += (r.qtyOrdered || 0) * cov;
          lfDelivered += (r.qtyDelivered || 0) * cov;
        }
      });

      const manualRows = sec.rows.filter((r) => r._hasManual);
      const manualDeliveredCost = manualRows.reduce(
        (s, r) => s + (r.amountDelivered || 0),
        0,
      );
      const manualOrderedCost = manualRows.reduce(
        (s, r) => s + (r.amountOrdered || 0),
        0,
      );

      summaries.set(secNum, {
        sectionNumber: secNum,
        sectionName: resolveName(secNum),
        itemCount: sec.rows.length,
        estCost,
        coCost,
        runningTotal,
        deliveredCost,
        nonCovNetBudget,
        orderedCost,
        covDeliveredCost,
        hasSF: sfEst > 0 || sfOrdered > 0 || sfDelivered > 0,
        sfEst,
        sfOrdered,
        sfDelivered,
        hasLF: lfEst > 0 || lfOrdered > 0 || lfDelivered > 0,
        lfEst,
        lfOrdered,
        lfDelivered,
        hasManual: manualRows.length > 0,
        manualCount: manualRows.length,
        manualOrderedCost,
        manualDeliveredCost,
      });
    });

    return { consolidatedRows: rows, sectionSummaries: summaries };
  }, [filtered, resolveName]);

  // filtered consolidated rows (for select-all in items tab)
  const filteredConsolidated = consolidatedRows;

  const filteredTotals = useMemo(() => {
    return consolidatedRows.reduce(
      (acc, r) => ({
        estimated: acc.estimated + (r.origCost || 0),
        coTotal: acc.coTotal + r._totalCOAmount,
        delivered: acc.delivered + (r.amountDelivered || 0),
      }),
      { estimated: 0, coTotal: 0, delivered: 0 },
    );
  }, [consolidatedRows]);

  if (loading)
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  if (error)
    return <div className="text-center py-10 text-red-400">{error}</div>;
  if (!po) return null;

  const originalEstimated = po.originalEstimatedAmount || 0;
  const changeOrderAmount = po.changeOrderAmount || 0;
  const runningTotal = po.runningTotal || originalEstimated + changeOrderAmount;
  const actualOrdered = po.actualAmountOrdered || 0;
  const actualDelivered = po.actualAmountDelivered || 0;
  const flag =
    actualDelivered === 0
      ? null
      : actualDelivered > runningTotal
        ? "red"
        : "green";

  const COL_SPAN = editMode ? 16 : 15; // edit: +1 checkbox col; both have type col

  const sourceFilterOptions = [
    { value: "all", label: "All" },
    { value: "budget", label: "Budget" },
    ...(hasCORows ? [{ value: "change_order", label: "CO Δ" }] : []),
    ...(hasManualRows ? [{ value: "manual", label: "Unbudgeted" }] : []),
  ];

  const renderEntries = [];
  consolidatedRows.forEach((row, idx) => {
    renderEntries.push({ kind: "item", row, idx, key: row._rowKey });
    if (expandedItem === row._rowKey)
      renderEntries.push({ kind: "history", row, key: `${row._rowKey}-hist` });
  });

  const sectionList = [...sectionSummaries.values()].sort((a, b) =>
    (a.sectionNumber || "").localeCompare(b.sectionNumber || ""),
  );

  // Bulk move: selected row keys → pick a target PO
  const selectedRows = consolidatedRows.filter((r) => selected.has(r._rowKey));

  return (
    <div className="max-w-full mx-auto px-4 py-6 space-y-5">
      {/* ── Header ── */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(`/Projects/${projectId}?tab=Purchase Orders`)}
          className="px-3 py-1.5 text-sm text-gray-200 hover:text-white bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors shrink-0"
        >
          ← Back
        </button>
        <div className="flex-1 min-w-0">
          {editMode && renaming ? (
            <InlineRename
              value={po.title}
              onSave={handleRename}
              onCancel={() => setRenaming(false)}
              saving={renameSaving}
            />
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-gray-800 truncate">
                {po.title}
              </h1>
              {editMode && (
                <button
                  onClick={() => setRenaming(true)}
                  className="shrink-0 text-[11px] text-gray-500 hover:text-orange-400 transition-colors"
                  title="Rename PO"
                >
                  ✏
                </button>
              )}
            </div>
          )}
          <p className="text-xs text-gray-800 mt-0.5">
            PO #{po.poNumber} · Floor:{" "}
            <span className="text-gray-800">{po.bidArea || "—"}</span>
            {po.sectionCount > 0 && (
              <span className="ml-2 text-gray-500">
                · {po.sectionCount} section{po.sectionCount !== 1 ? "s" : ""}
              </span>
            )}
            <span
              className={cls(
                "ml-2 px-1.5 py-0.5 text-[10px] font-semibold rounded-full",
                po.status === "active"
                  ? "bg-green-700 text-green-200"
                  : "bg-gray-700 text-gray-400",
              )}
            >
              {po.status}
            </span>
            {loadingHistory && (
              <span className="ml-2 text-[10px] text-gray-600 animate-pulse">
                Loading history…
              </span>
            )}
          </p>
        </div>
        {/* Edit mode toggle */}
        <button
          onClick={() => {
            setEditMode((prev) => {
              if (prev) {
                setRenaming(false);
                setSelected(new Set());
              }
              return !prev;
            });
          }}
          className={cls(
            "px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors shrink-0",
            editMode
              ? "bg-orange-500 text-white hover:bg-orange-600"
              : "bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white",
          )}
        >
          {editMode ? "✓ Done" : "✏ Edit"}
        </button>
      </div>

      {/* ── PO-level summary cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <SummaryCard
          label="Original Estimated"
          value={originalEstimated}
          color="text-white"
        />
        <SummaryCard
          label="Change Orders"
          value={changeOrderAmount}
          color="text-purple-300"
        />
        <SummaryCard
          label="Running Total"
          value={runningTotal}
          color="text-orange-300"
        />
        <SummaryCard
          label="Actual Ordered"
          value={actualOrdered}
          color="text-yellow-300"
          sub={`${po.actualQtyOrdered || 0} units`}
        />
        <SummaryCard
          label="Actual Delivered"
          value={actualDelivered}
          color={
            flag === "green"
              ? "text-green-300"
              : flag === "red"
                ? "text-red-400"
                : "text-blue-300"
          }
          sub={`${po.actualQtyDelivered || 0} delivered · ${po.actualQtyPending || 0} pending`}
          flag={flag}
        />
        <div
          className={cls(
            "rounded-xl border p-4",
            flag === "green"
              ? "bg-green-950 border-green-700"
              : flag === "red"
                ? "bg-red-950 border-red-700"
                : "bg-gray-800 border-gray-700",
          )}
        >
          <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
            Status {flag === "green" ? "✓" : flag === "red" ? "⚠" : ""}
          </p>
          <p
            className={cls(
              "text-sm font-bold",
              flag === "green"
                ? "text-green-400"
                : flag === "red"
                  ? "text-red-400"
                  : "text-gray-400",
            )}
          >
            {actualDelivered === 0
              ? actualOrdered > 0
                ? "Ordered, awaiting delivery"
                : "Awaiting orders"
              : flag === "green"
                ? "Under budget"
                : flag === "red"
                  ? "Over budget"
                  : "On budget"}
          </p>
          {actualDelivered > 0 && (
            <p
              className={cls(
                "text-xs mt-0.5",
                flag === "green" ? "text-green-500" : "text-red-500",
              )}
            >
              Δ ${fmt(Math.abs(runningTotal - actualDelivered))}
            </p>
          )}
          {actualOrdered > 0 && actualDelivered < actualOrdered && (
            <p className="text-xs text-yellow-500 mt-0.5">
              ${fmt(actualOrdered - actualDelivered)} pending delivery
            </p>
          )}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex items-center gap-1 border-b border-gray-700">
        {[
          {
            id: "summary",
            label: "Section Summary",
            count: sectionList.length,
          },
          { id: "items", label: "Line Items", count: consolidatedRows.length },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cls(
              "px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px",
              activeTab === tab.id
                ? "border-orange-500 text-orange-400"
                : "border-transparent text-gray-500 hover:text-gray-300",
            )}
          >
            {tab.label}
            <span
              className={cls(
                "ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full font-semibold",
                activeTab === tab.id
                  ? "bg-orange-500/20 text-orange-300"
                  : "bg-gray-700 text-gray-500",
              )}
            >
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          SUMMARY TAB
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === "summary" && (
        <div className="space-y-3">
          {sectionList.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-10">
              No sections found.
            </p>
          ) : (
            sectionList.map((sec) => {
              const variance = sec.deliveredCost - sec.runningTotal;
              const nonCovDelivered = sec.deliveredCost - sec.covDeliveredCost;
              const orderedPct =
                sec.nonCovNetBudget > 0
                  ? Math.min(100, (sec.orderedCost / sec.nonCovNetBudget) * 100)
                  : 0;
              const nonCovDeliveredPct =
                sec.nonCovNetBudget > 0
                  ? Math.min(100, (nonCovDelivered / sec.nonCovNetBudget) * 100)
                  : 0;
              const isOver = sec.deliveredCost > 0 && variance > 0;
              const isUnder = sec.deliveredCost > 0 && variance <= 0;

              return (
                <div
                  key={sec.sectionNumber}
                  className={cls(
                    "border rounded-xl overflow-hidden",
                    isOver
                      ? "bg-gray-800 border-red-700/60"
                      : isUnder
                        ? "bg-gray-800 border-green-700/40"
                        : "bg-gray-800 border-gray-700",
                  )}
                >
                  <div
                    className={cls(
                      "flex items-center gap-3 px-4 py-3 border-b",
                      isOver
                        ? "border-red-700/40"
                        : isUnder
                          ? "border-green-700/30"
                          : "border-gray-700/60",
                    )}
                  >
                    <span className="text-[11px] font-mono text-indigo-400 bg-indigo-900/50 px-2 py-0.5 rounded shrink-0">
                      §{sec.sectionNumber}
                    </span>
                    <span className="text-sm font-semibold text-white flex-1 truncate">
                      {sec.sectionName || sec.sectionNumber}
                    </span>
                    <span className="text-[10px] text-gray-500 shrink-0">
                      {sec.itemCount} item{sec.itemCount !== 1 ? "s" : ""}
                    </span>
                    {isOver && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-900/60 text-red-300 shrink-0">
                        ⚠ Over ${fmt(Math.abs(variance))}
                      </span>
                    )}
                    {isUnder && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-900/50 text-green-400 shrink-0">
                        ✓ Under ${fmt(Math.abs(variance))}
                      </span>
                    )}
                    <button
                      onClick={() => {
                        setSectionFilter(sec.sectionNumber);
                        setActiveTab("items");
                      }}
                      className="text-[10px] text-orange-400 hover:text-orange-300 shrink-0 px-2 py-0.5 rounded bg-orange-900/20 hover:bg-orange-900/40 transition-colors"
                    >
                      View items →
                    </button>
                  </div>
                  <div className="px-4 py-2 flex items-center gap-4 flex-wrap text-sm">
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-500">Est</span>
                      <span className="font-mono font-bold text-blue-300 text-base">
                        ${fmt(sec.estCost)}
                      </span>
                      {sec.coCost !== 0 && (
                        <span
                          className={cls(
                            "font-mono font-semibold text-sm",
                            sec.coCost >= 0
                              ? "text-purple-400"
                              : "text-red-400",
                          )}
                        >
                          {sec.coCost >= 0 ? "+" : ""}${fmt(sec.coCost)} CO
                        </span>
                      )}
                    </div>
                    <span className="text-gray-700">·</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-500">Net</span>
                      <span className="font-mono font-bold text-orange-300 text-base">
                        ${fmt(sec.runningTotal)}
                      </span>
                    </div>
                    <span className="text-gray-700">·</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-500">Ordered</span>
                      <span className="font-mono font-bold text-yellow-300 text-base">
                        ${fmt(sec.orderedCost)}
                      </span>
                      {sec.nonCovNetBudget > 0 && sec.orderedCost > 0 && (
                        <span className="text-gray-600 text-xs">
                          ({orderedPct.toFixed(0)}%)
                        </span>
                      )}
                    </div>
                    <span className="text-gray-700">·</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-500">Delivered</span>
                      <span
                        className={cls(
                          "font-mono font-bold text-base",
                          isOver
                            ? "text-red-400"
                            : isUnder
                              ? "text-green-300"
                              : "text-gray-300",
                        )}
                      >
                        ${fmt(sec.deliveredCost)}
                      </span>
                      {sec.deliveredCost > 0 && (
                        <span
                          className={cls(
                            "font-mono font-semibold text-sm",
                            isOver ? "text-red-400" : "text-green-500",
                          )}
                        >
                          ({isOver ? "↑" : "↓"}${fmt(Math.abs(variance))})
                        </span>
                      )}
                    </div>
                  </div>
                  {(sec.orderedCost > 0 || sec.deliveredCost > 0) && (
                    <div className="px-4 pb-3 space-y-1.5">
                      {sec.orderedCost > 0 && sec.nonCovNetBudget > 0 && (
                        <ProgressBar
                          label="$ Ordered"
                          pct={Math.min(
                            100,
                            (sec.orderedCost / sec.nonCovNetBudget) * 100,
                          )}
                          colorClass={
                            sec.orderedCost >= sec.nonCovNetBudget
                              ? "bg-yellow-400"
                              : "bg-yellow-600"
                          }
                        />
                      )}
                      {sec.deliveredCost > 0 && sec.nonCovNetBudget > 0 && (
                        <ProgressBar
                          label="$ Delivered"
                          pct={Math.min(
                            100,
                            (sec.deliveredCost / sec.nonCovNetBudget) * 100,
                          )}
                          colorClass={
                            sec.deliveredCost >= sec.nonCovNetBudget
                              ? isOver
                                ? "bg-red-500"
                                : "bg-green-500"
                              : "bg-green-700"
                          }
                        />
                      )}
                    </div>
                  )}
                  {sec.hasSF && (
                    <CoveragePanelPanel
                      label="SF Coverage"
                      uom="SF"
                      colorScheme="sf"
                      est={sec.sfEst}
                      ordered={sec.sfOrdered}
                      delivered={sec.sfDelivered}
                    />
                  )}
                  {sec.hasLF && (
                    <CoveragePanelPanel
                      label="LF Coverage"
                      uom="LF"
                      colorScheme="lf"
                      est={sec.lfEst}
                      ordered={sec.lfOrdered}
                      delivered={sec.lfDelivered}
                    />
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          ITEMS TAB
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === "items" && (
        <>
          {/* Legend */}
          <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-gray-500 inline-block" />
              Budget
            </span>
            {hasCORows && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-purple-500 inline-block" />
                CO Δ merged into row
              </span>
            )}
            {hasManualRows && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" />
                Unbudgeted merged into row
              </span>
            )}
            {editMode && siblingPOs.length > 0 && (
              <span className="flex items-center gap-1.5 text-orange-400">
                <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" />
                Select rows to move between POs
              </span>
            )}
          </div>

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search items…"
              className="px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none w-52"
            />
            <select
              value={sectionFilter}
              onChange={(e) => setSectionFilter(e.target.value)}
              className="px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:border-orange-500 focus:outline-none"
            >
              {uniqueSections.map((s) => (
                <option key={s} value={s}>
                  {s === "all" ? "All Sections" : resolveLabel(s)}
                </option>
              ))}
            </select>
            {sectionFilter !== "all" && (
              <button
                onClick={() => setSectionFilter("all")}
                className="px-2 py-1.5 text-xs text-gray-400 hover:text-white bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
              >
                ✕ Clear
              </button>
            )}
            <div className="flex rounded overflow-hidden border border-gray-600">
              {sourceFilterOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSourceFilter(opt.value)}
                  className={cls(
                    "px-2 py-1 text-xs font-medium transition-colors",
                    sourceFilter === opt.value
                      ? "bg-orange-500 text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600",
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <span className="text-xs text-gray-500">
              {consolidatedRows.length} of {items.length} items
            </span>
            <span className="ml-auto text-xs text-gray-600">
              ▶ Click row to expand · ☐ Select to move
            </span>
          </div>

          {/* ── Bulk move bar ── */}
          {editMode && selected.size > 0 && (
            <div className="flex items-center gap-3 flex-wrap px-4 py-2.5 bg-orange-950/50 border border-orange-700/50 rounded-xl">
              <span className="text-sm font-semibold text-orange-300">
                {selected.size} row{selected.size !== 1 ? "s" : ""} selected
              </span>
              {siblingPOs.length > 0 ? (
                <>
                  <span className="text-xs text-gray-400">Move to:</span>
                  {siblingPOs.map((p) => (
                    <button
                      key={p._id}
                      onClick={() => handleMoveRows([...selected], p._id)}
                      disabled={moving}
                      className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg border border-gray-600 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
                    >
                      {moving && (
                        <span className="animate-spin inline-block w-3 h-3 border-2 border-white/30 border-t-white rounded-full" />
                      )}
                      {p.title}
                    </button>
                  ))}
                </>
              ) : (
                <span className="text-xs text-gray-500 italic">
                  No other POs on this project to move to
                </span>
              )}
              <button
                onClick={() => setSelected(new Set())}
                className="ml-auto text-xs text-gray-500 hover:text-white transition-colors"
              >
                ✕ Clear selection
              </button>
            </div>
          )}

          {/* Section context badge */}
          {sectionFilter !== "all" &&
            sectionSummaries.has(sectionFilter) &&
            (() => {
              const sec = sectionSummaries.get(sectionFilter);
              return (
                <div className="flex items-center gap-3 flex-wrap bg-indigo-950 border border-indigo-800 rounded-xl px-4 py-2.5">
                  <span className="text-[11px] font-mono text-indigo-400 bg-indigo-900/50 px-2 py-0.5 rounded shrink-0">
                    §{sec.sectionNumber}
                  </span>
                  <span className="text-sm font-semibold text-indigo-100">
                    {sec.sectionName || sec.sectionNumber}
                  </span>
                  <div className="h-4 border-l border-indigo-700/40" />
                  <span className="text-xs text-gray-400">
                    Est:{" "}
                    <span className="text-blue-300 font-mono font-semibold">
                      ${fmt(sec.estCost)}
                    </span>
                  </span>
                  {sec.coCost !== 0 && (
                    <span className="text-xs text-gray-400">
                      CO:{" "}
                      <span
                        className={cls(
                          "font-mono font-semibold",
                          sec.coCost >= 0 ? "text-purple-300" : "text-red-400",
                        )}
                      >
                        {sec.coCost >= 0 ? "+" : ""}${fmt(sec.coCost)}
                      </span>
                    </span>
                  )}
                  <span className="text-xs text-gray-400">
                    Net:{" "}
                    <span className="text-orange-300 font-mono font-semibold">
                      ${fmt(sec.runningTotal)}
                    </span>
                  </span>
                  <span className="text-xs text-gray-400">
                    Delivered:{" "}
                    <span className="text-green-300 font-mono font-semibold">
                      ${fmt(sec.deliveredCost)}
                    </span>
                  </span>
                  {sec.hasSF && (
                    <>
                      <div className="h-4 border-l border-indigo-700/40" />
                      <span className="text-xs text-gray-400">
                        SF — Est:{" "}
                        <span className="text-blue-200 font-mono">
                          {fmtQty(sec.sfEst)}
                        </span>{" "}
                        · Ordered:{" "}
                        <span className="text-yellow-300 font-mono">
                          {fmtQty(sec.sfOrdered)}
                        </span>{" "}
                        · Delivered:{" "}
                        <span className="text-green-300 font-mono">
                          {fmtQty(sec.sfDelivered)}
                        </span>
                      </span>
                    </>
                  )}
                  {sec.hasLF && (
                    <>
                      <div className="h-4 border-l border-indigo-700/40" />
                      <span className="text-xs text-gray-400">
                        LF — Est:{" "}
                        <span className="text-blue-200 font-mono">
                          {fmtQty(sec.lfEst)}
                        </span>{" "}
                        · Ordered:{" "}
                        <span className="text-yellow-300 font-mono">
                          {fmtQty(sec.lfOrdered)}
                        </span>{" "}
                        · Delivered:{" "}
                        <span className="text-green-300 font-mono">
                          {fmtQty(sec.lfDelivered)}
                        </span>
                      </span>
                    </>
                  )}
                </div>
              );
            })()}

          {/* Table */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-900 sticky top-0 z-20">
                  <tr>
                    {/* Checkbox col */}
                    <th className="px-2 py-2.5 w-8 text-center">
                      {editMode && (
                        <input
                          type="checkbox"
                          checked={
                            filteredConsolidated.length > 0 &&
                            selected.size === filteredConsolidated.length
                          }
                          onChange={toggleSelectAll}
                          className="accent-orange-500 w-3.5 h-3.5 cursor-pointer"
                          title="Select all"
                        />
                      )}
                    </th>
                    <th className="px-2 py-2.5 w-6" />
                    <th className="px-3 py-2.5 text-left   text-gray-400 font-medium">
                      Code
                    </th>
                    <th className="px-3 py-2.5 text-left   text-gray-400 font-medium">
                      Section
                    </th>
                    <th className="px-3 py-2.5 text-left   text-gray-400 font-medium min-w-[200px]">
                      Description
                    </th>
                    <th className="px-3 py-2.5 text-left   text-gray-400 font-medium">
                      Size
                    </th>
                    <th className="px-3 py-2.5 text-right  text-gray-400 font-medium">
                      Net Qty
                      <span className="block text-[9px] text-gray-600 font-normal normal-case">
                        budget + CO Δ + unbudgeted
                      </span>
                    </th>
                    <th className="px-3 py-2.5 text-right  text-gray-400 font-medium">
                      Unit Cost
                    </th>
                    <th className="px-3 py-2.5 text-center text-gray-400 font-medium">
                      UOM
                    </th>
                    <th className="px-3 py-2.5 text-right  text-gray-400 font-medium">
                      Net Total
                      <span className="block text-[9px] text-gray-600 font-normal normal-case">
                        budget + CO + unbudgeted
                      </span>
                    </th>
                    <th className="px-3 py-2.5 text-right  text-gray-400 font-medium">
                      Ordered
                    </th>
                    <th className="px-3 py-2.5 text-right  text-gray-400 font-medium">
                      Pending
                    </th>
                    <th className="px-3 py-2.5 text-right  text-gray-400 font-medium">
                      Delivered
                    </th>
                    <th className="px-3 py-2.5 text-right  text-gray-400 font-medium">
                      Delivered $
                    </th>
                    <th className="px-3 py-2.5 text-left   text-gray-400 font-medium min-w-[120px]">
                      Progress
                    </th>
                    <th className="px-3 py-2.5 text-left   text-gray-400 font-medium min-w-[130px]">
                      {editMode && siblingPOs.length > 0
                        ? "Move / Type"
                        : "Type"}
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-700/50">
                  {renderEntries.map((entry) => {
                    if (entry.kind === "history") {
                      return (
                        <ExpandedDetailPanel
                          key={entry.key}
                          row={entry.row}
                          deliveryHistory={deliveryHistory}
                          colSpan={COL_SPAN}
                        />
                      );
                    }

                    const { row, idx } = entry;
                    const isCONew = row._type === "change_order_new";
                    const isManual = row._type === "manual";
                    const isBudget = row._type === "budget";
                    const isExpanded = expandedItem === row._rowKey;
                    const isChecked = selected.has(row._rowKey);
                    const hasHistory = row._allIds.some(
                      (id) => (deliveryHistory[id] || []).length > 0,
                    );
                    const hasVariants = (row._variants || []).length > 1;
                    const netQtyChanged =
                      row._totalCODeltaQty !== 0 || row._manualQty !== 0;

                    return (
                      <tr
                        key={entry.key}
                        onClick={() => toggleRow(row._rowKey)}
                        className={cls(
                          "cursor-pointer transition-colors hover:bg-gray-600/30",
                          idx % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20",
                          isCONew ? "border-l-2 border-purple-600/60" : "",
                          isManual ? "border-l-2 border-yellow-600/60" : "",
                          isExpanded ? "bg-indigo-900/20" : "",
                          editMode && isChecked
                            ? "bg-orange-900/15 border-l-2 border-orange-500/50"
                            : "",
                        )}
                      >
                        {/* Checkbox */}
                        <td
                          className="px-2 py-2.5 text-center w-8"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {editMode && (
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => toggleSelect(e, row._rowKey)}
                              className="accent-orange-500 w-3.5 h-3.5 cursor-pointer"
                            />
                          )}
                        </td>

                        {/* Expand arrow */}
                        <td className="px-1 py-2.5 text-center w-6">
                          <span
                            className={cls(
                              "inline-block text-[10px] transition-transform duration-150",
                              isExpanded
                                ? "rotate-90 text-orange-400"
                                : hasHistory || hasVariants
                                  ? "text-gray-400"
                                  : "text-gray-700",
                            )}
                          >
                            ▶
                          </span>
                        </td>

                        <td className="px-3 py-2.5 font-mono text-orange-400">
                          {row.itemCode || "—"}
                        </td>

                        <td className="px-3 py-2.5">
                          <span className="text-[9px] font-mono text-indigo-400 bg-indigo-900/40 px-1.5 py-0.5 rounded whitespace-nowrap">
                            {row.sectionNumber || "—"}
                          </span>
                        </td>

                        <td className="px-3 py-2.5 text-gray-200">
                          {row.costCodeDescription ||
                            row.itemDescription ||
                            "—"}
                          {row._hasCOs && (
                            <span className="ml-1.5 text-[9px] bg-purple-800/50 text-purple-300 px-1 py-0.5 rounded font-semibold whitespace-nowrap">
                              CO {row._totalCODeltaQty > 0 ? "+" : ""}
                              {fmtQty(row._totalCODeltaQty)}
                            </span>
                          )}
                          {isCONew && (
                            <span className="ml-1.5 text-[9px] bg-purple-600/60 text-purple-100 px-1 py-0.5 rounded font-semibold">
                              CO NEW
                            </span>
                          )}
                          {row._hasManual && (
                            <span className="ml-1.5 text-[9px] bg-yellow-600/60 text-yellow-100 px-1 py-0.5 rounded font-semibold">
                              {isManual
                                ? "UNBUDGETED"
                                : `+${fmtQty(row._manualQty)} UNBUDGETED`}
                            </span>
                          )}
                          {hasVariants && (
                            <span className="ml-1.5 text-[9px] bg-gray-700 text-gray-300 px-1 py-0.5 rounded whitespace-nowrap">
                              {row._variants.length} sizes/prices
                            </span>
                          )}
                          {row._bidAreas?.length > 1 && (
                            <span className="ml-1.5 text-[9px] bg-gray-700 text-gray-300 px-1 py-0.5 rounded whitespace-nowrap">
                              {row._bidAreas.join(" · ")}
                            </span>
                          )}
                          {row._bidAreas?.length === 1 && (
                            <span className="ml-1.5 text-[9px] text-gray-500 whitespace-nowrap">
                              {row._bidAreas[0]}
                            </span>
                          )}
                        </td>

                        <td className="px-3 py-2.5 text-gray-200">
                          {row.size || "—"}
                        </td>

                        <td className="px-3 py-2.5 text-right font-mono">
                          {netQtyChanged ? (
                            <span className="flex flex-col items-end leading-tight">
                              <span className="text-orange-300 font-semibold">
                                {fmtQty(row.netQty)}
                              </span>
                              <span className="text-[9px] text-gray-500">
                                {fmtQty(row.origQty)}{" "}
                                {row._totalCODeltaQty !== 0 && (
                                  <span
                                    className={
                                      row._totalCODeltaQty > 0
                                        ? "text-green-500"
                                        : "text-red-500"
                                    }
                                  >
                                    {row._totalCODeltaQty > 0 ? "+" : ""}
                                    {fmtQty(row._totalCODeltaQty)} CO
                                  </span>
                                )}
                                {row._manualQty !== 0 && (
                                  <span className="text-yellow-500">
                                    {" "}
                                    +{fmtQty(row._manualQty)} unb.
                                  </span>
                                )}
                              </span>
                            </span>
                          ) : (
                            <span className="text-gray-300">
                              {fmtQty(row.netQty)}
                            </span>
                          )}
                        </td>

                        <td className="px-3 py-2.5 text-right text-gray-300 font-mono">
                          {isCoverageUom(row.uom) && row.coveragePerUnit > 1 ? (
                            <span className="flex flex-col items-end leading-tight">
                              <span className="text-gray-200 font-semibold">
                                ${fmt(row.unitCost || 0)}
                                <span className="text-[10px] text-gray-500 font-normal ml-0.5">
                                  /
                                  {isSF(row.uom)
                                    ? "SF"
                                    : isLF(row.uom)
                                      ? "LF"
                                      : (row.uom || "").toUpperCase()}
                                </span>
                              </span>
                              <span className="text-[11px] text-gray-500">
                                $
                                {fmt(
                                  (row.unitCost || 0) *
                                    (row.coveragePerUnit || 1),
                                )}
                                /sheet
                              </span>
                            </span>
                          ) : (
                            <span>${fmt(row.unitCost || 0)}</span>
                          )}
                        </td>

                        <td className="px-3 py-2.5 text-center text-gray-200">
                          {isCoverageUom(row.uom) ? (
                            <span className="flex flex-col items-center leading-tight">
                              <span className="text-gray-200 font-semibold">
                                PC
                              </span>
                              <span className="text-[10px] text-gray-500">
                                ×{row.coveragePerUnit || 1}{" "}
                                {isSF(row.uom)
                                  ? "SF"
                                  : isLF(row.uom)
                                    ? "LF"
                                    : (row.uom || "").toUpperCase()}
                              </span>
                            </span>
                          ) : (
                            row.uom || "—"
                          )}
                        </td>

                        <td className="px-3 py-2.5 text-right font-mono">
                          {row.netCost === 0 && row.origCost === 0 ? (
                            <span className="text-yellow-500 text-[10px]">
                              $0 est.
                            </span>
                          ) : (
                            <span className="flex flex-col items-end leading-tight">
                              <span
                                className={cls(
                                  "font-semibold",
                                  netQtyChanged
                                    ? "text-orange-300"
                                    : "text-blue-300",
                                )}
                              >
                                ${fmt(row.netCost)}
                              </span>
                              {row._hasCOs && row._totalCOAmount !== 0 && (
                                <span
                                  className={cls(
                                    "text-[9px]",
                                    row._totalCOAmount > 0
                                      ? "text-purple-400"
                                      : "text-red-400",
                                  )}
                                >
                                  {row._totalCOAmount > 0 ? "+" : ""}$
                                  {fmt(row._totalCOAmount)} CO
                                </span>
                              )}
                              {row._hasManual && row._manualCost !== 0 && (
                                <span className="text-[9px] text-yellow-500">
                                  +${fmt(row._manualCost)} unb.
                                </span>
                              )}
                            </span>
                          )}
                        </td>

                        <td className="px-3 py-2.5 text-right text-yellow-300 font-mono">
                          {fmtQty(row.qtyOrdered)}
                        </td>
                        <td className="px-3 py-2.5 text-right text-orange-300 font-mono">
                          {fmtQty(row.qtyPending)}
                        </td>
                        <td className="px-3 py-2.5 text-right text-green-300 font-mono">
                          {fmtQty(row.qtyDelivered)}
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono font-semibold">
                          {row.amountDelivered > 0 ? (
                            <span className="text-blue-300">
                              ${fmt(row.amountDelivered)}
                            </span>
                          ) : (
                            <span className="text-gray-700">—</span>
                          )}
                        </td>

                        <td className="px-3 py-2.5">
                          <QtyBar
                            ordered={row.qtyOrdered}
                            delivered={row.qtyDelivered}
                            netQty={row.netQty}
                          />
                        </td>

                        {/* Move / Type combined col */}
                        <td
                          className="px-2 py-2 min-w-[130px]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {editMode ? (
                            <div className="flex flex-col gap-1">
                              {/* Type badge */}
                              <div>
                                {isCONew ? (
                                  <span className="text-[9px] bg-purple-700/60 text-purple-100 px-1.5 py-0.5 rounded font-semibold">
                                    CO
                                  </span>
                                ) : isManual ? (
                                  <span className="text-[9px] bg-yellow-700/60 text-yellow-100 px-1.5 py-0.5 rounded font-semibold">
                                    MANUAL
                                  </span>
                                ) : row._hasCOs || row._hasManual ? (
                                  <span className="text-[9px] bg-indigo-700/60 text-indigo-100 px-1.5 py-0.5 rounded font-semibold">
                                    B+CO/M
                                  </span>
                                ) : (
                                  <span className="text-[9px] text-gray-600">
                                    Budget
                                  </span>
                                )}
                              </div>
                              {/* Move dropdown */}
                              <MoveDropdown
                                rowKey={row._rowKey}
                                siblingPOs={siblingPOs}
                                onMove={(rowKey, targetPoId) =>
                                  handleMoveRows([rowKey], targetPoId)
                                }
                                moving={moving}
                              />
                            </div>
                          ) : /* Read-only type badge */
                          isCONew ? (
                            <span className="text-[9px] bg-purple-700/60 text-purple-100 px-1.5 py-0.5 rounded font-semibold">
                              CO
                            </span>
                          ) : isManual ? (
                            <span className="text-[9px] bg-yellow-700/60 text-yellow-100 px-1.5 py-0.5 rounded font-semibold">
                              MANUAL
                            </span>
                          ) : row._hasCOs || row._hasManual ? (
                            <span className="text-[9px] bg-indigo-700/60 text-indigo-100 px-1.5 py-0.5 rounded font-semibold">
                              B+CO/M
                            </span>
                          ) : (
                            <span className="text-[9px] text-gray-600">
                              Budget
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {consolidatedRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={COL_SPAN}
                        className="px-3 py-10 text-center text-gray-500"
                      >
                        {items.length === 0
                          ? "No items on this PO yet."
                          : "No items match your filters."}
                      </td>
                    </tr>
                  )}
                </tbody>

                <tfoot className="bg-gray-900 sticky bottom-0 border-t border-gray-600">
                  <tr>
                    <td
                      colSpan={9}
                      className="px-3 py-2.5 text-xs text-gray-400"
                    >
                      {consolidatedRows.length} rows shown
                      {editMode && selected.size > 0 && (
                        <span className="ml-2 text-orange-400 font-semibold">
                          · {selected.size} selected
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      <span className="text-sm font-bold text-blue-300">
                        ${fmt(filteredTotals.estimated)}
                      </span>
                      {filteredTotals.coTotal !== 0 && (
                        <span
                          className={cls(
                            "block text-[10px] font-semibold",
                            filteredTotals.coTotal >= 0
                              ? "text-purple-400"
                              : "text-red-400",
                          )}
                        >
                          {filteredTotals.coTotal >= 0 ? "+" : ""}$
                          {fmt(filteredTotals.coTotal)} CO
                        </span>
                      )}
                    </td>
                    <td colSpan={3} />
                    <td className="px-3 py-2.5 text-right text-sm font-bold text-green-300 font-mono">
                      ${fmt(filteredTotals.delivered)}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
